import { mergeStylesForMail } from '@features/modal-invitation/lib/mergeStylesForMail';
import { getTextFromHtml } from '@features/modal-invitation/lib/getTextFromHtml';
import { formatLocalDateToUtc } from '@shared/libs/format-local-date';
import { SendContentType } from '@pages/event/api/useSendMessages';
import type { SendInvitationMessagesInput } from '../api/useSendInvitationMessages';
import { SendInvitationChannel } from '../api/useSendInvitationMessages';
import { INVITATION_TYPE_EMAIL, INVITATION_TYPE_SMS, INVITATION_TYPE_VK_TEAMS } from './invitation-types';
import type { SelectValue } from '@shared/ui/select';
import {
  mapSelectedChannelsToSendChannels,
  mapSelectedTicketChannelsToDeliveryMethods,
} from './invitation-channel-options';
import {
  OUTLOOK_MEETING_DEFAULT_END_TIME,
  OUTLOOK_MEETING_DEFAULT_START_TIME,
  type OutlookTimeSlotOption,
} from '@features/outlook-meeting';

export const INVITATION_DELIVERY_TIME = '23:59';
export const TICKET_DELIVERY_TIME = '11:00';

const INVITATION_DEFAULT_TIME = `T${INVITATION_DELIVERY_TIME}:59`;
const TICKET_DEFAULT_TIME = `T${TICKET_DELIVERY_TIME}:00`;

type InvitationSendPayloadInput = {
  ticketIds: number[];
  selectedInvitationChannels: SelectValue[];
  invitationText: string;
  invitationSubject: string;
  invitationExpiresAt: Date;
  inviteAcceptionInfo: string;
  isTicketSettingsCollapsed: boolean;
  isSkipTicketSendChecked: boolean;
  selectedTicketChannels: SelectValue[];
  ticketText: string;
  ticketSubject: string;
  lockedTicketSubject?: string | null;
  lockedTicketPlainMessage?: string | null;
  lockedTicketEmailMessage?: string | null;
  ticketDate: Date | null;
  isOutlookMeetingEnabled: boolean;
  outlookMeetingDate: Date | null;
  outlookMeetingStartTime: OutlookTimeSlotOption | null;
  outlookMeetingEndTime: OutlookTimeSlotOption | null;
  outlookMeetingLocationOrLink: string;
  outlookMeetingComment: string;
};

export const invitationSendPayload = ({
  ticketIds,
  selectedInvitationChannels,
  invitationText,
  invitationSubject,
  invitationExpiresAt,
  inviteAcceptionInfo,
  isTicketSettingsCollapsed,
  isSkipTicketSendChecked,
  selectedTicketChannels,
  ticketText,
  ticketSubject,
  lockedTicketSubject,
  lockedTicketPlainMessage,
  lockedTicketEmailMessage,
  ticketDate,
  isOutlookMeetingEnabled,
  outlookMeetingDate,
  outlookMeetingStartTime,
  outlookMeetingEndTime,
  outlookMeetingLocationOrLink,
  outlookMeetingComment,
}: InvitationSendPayloadInput): SendInvitationMessagesInput => {
  const channel = mapSelectedChannelsToSendChannels(selectedInvitationChannels);
  const invitationTextPlain = getTextFromHtml(invitationText).trim();
  const invitationExpiresAtUtc = formatLocalDateToUtc(invitationExpiresAt, INVITATION_DEFAULT_TIME);
  const hasEmail = channel.includes(SendInvitationChannel.Email);
  const hasSmsOrVk = channel.includes(SendInvitationChannel.SMS) || channel.includes(SendInvitationChannel.VK);

  const effectiveTicketDeliveryMethod =
    !isTicketSettingsCollapsed && selectedTicketChannels.length
      ? mapSelectedTicketChannelsToDeliveryMethods(selectedTicketChannels)
      : null;
  const sendTicketAfterAcceptAtUtc =
    !isTicketSettingsCollapsed && !isSkipTicketSendChecked && ticketDate
      ? formatLocalDateToUtc(ticketDate, TICKET_DEFAULT_TIME)
      : null;

  const hasTicketEmail = selectedTicketChannels.includes(INVITATION_TYPE_EMAIL);
  const hasTicketSmsOrVk =
    selectedTicketChannels.includes(INVITATION_TYPE_SMS) || selectedTicketChannels.includes(INVITATION_TYPE_VK_TEAMS);

  const ticketTextPlain =
    lockedTicketPlainMessage?.trim() || getTextFromHtml(lockedTicketEmailMessage ?? ticketText).trim();

  const hasAnyTicketChannel = hasTicketEmail || hasTicketSmsOrVk;
  const ticketDeliveryMessage = !isTicketSettingsCollapsed && hasAnyTicketChannel ? ticketTextPlain : '';
  const emailTicketDeliveryMessage =
    !isTicketSettingsCollapsed && hasTicketEmail ? lockedTicketEmailMessage ?? mergeStylesForMail(ticketText) : '';

  const outlookMeetingStartTimeValue = outlookMeetingStartTime?.value ?? OUTLOOK_MEETING_DEFAULT_START_TIME;
  const outlookMeetingEndTimeValue = outlookMeetingEndTime?.value ?? OUTLOOK_MEETING_DEFAULT_END_TIME;

  const outlookCalendarMeetingFields = isOutlookMeetingEnabled
    ? {
        calendarMeetingDescription: outlookMeetingComment,
        calendarMeetingPoint: outlookMeetingLocationOrLink,
        calendarMeetingDateStart: outlookMeetingDate
          ? formatLocalDateToUtc(outlookMeetingDate, `T${outlookMeetingStartTimeValue}:00`)
          : null,
        calendarMeetingDateEnd: outlookMeetingDate
          ? formatLocalDateToUtc(outlookMeetingDate, `T${outlookMeetingEndTimeValue}:00`)
          : null,
      }
    : {
        calendarMeetingDescription: null,
        calendarMeetingPoint: null,
        calendarMeetingDateStart: null,
        calendarMeetingDateEnd: null,
      };

  return {
    ticketIds,
    messageTemplate: hasSmsOrVk ? invitationTextPlain : '',
    emailMessageTemplate: hasEmail ? mergeStylesForMail(invitationText) : '',
    contentType: SendContentType.Invite,
    channel,
    eventSettings: {
      invitationExpiresAt: invitationExpiresAtUtc,
      skipTicketsOnAccept: isTicketSettingsCollapsed,
      ticketDeliveryMethod: effectiveTicketDeliveryMethod,
      ticketDeliveryMessage,
      emailTicketDeliveryMessage,
      sendTicketAfterAcceptAt: sendTicketAfterAcceptAtUtc,
      inviteAcceptionInfo: inviteAcceptionInfo.trim(),
      invitationEmailSubject: hasEmail ? invitationSubject.trim() : '',
      // Без канала E-mail тему не передаём: отсутствие параметра сохраняет заданную ранее.
      ...(!isTicketSettingsCollapsed && hasTicketEmail
        ? { ticketEmailSubject: (lockedTicketSubject ?? ticketSubject).trim() }
        : {}),
      ...outlookCalendarMeetingFields,
    },
  };
};
